import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Search, Globe } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { tierMeta, type AlertTier } from "@/lib/alertStyles";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

interface PageRow {
  id: string;
  key: string;
  name: string;
  parent: string | null;
}

export interface AlertEditorValue {
  id?: string;
  tier: AlertTier;
  title: string;
  description: string;
  app_wide: boolean;
  page_keys: string[];
}

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  initial?: AlertEditorValue | null;
  onSaved: () => void;
}

const tiers: AlertTier[] = ["notification", "alert", "urgent"];

const empty: AlertEditorValue = {
  tier: "notification",
  title: "",
  description: "",
  app_wide: true,
  page_keys: [],
};

export function AlertEditorModal({ open, onOpenChange, initial, onSaved }: Props) {
  const { user } = useAuth();
  const [value, setValue] = useState<AlertEditorValue>(empty);
  const [pages, setPages] = useState<PageRow[]>([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setValue(initial ? { ...initial } : { ...empty });
      setSearch("");
    }
  }, [open, initial]);

  useEffect(() => {
    supabase
      .from("app_pages" as any)
      .select("id, key, name, parent")
      .order("sort_order")
      .then(({ data }) => {
        if (data) setPages(data as any);
      });
  }, []);

  const filteredPages = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pages;
    return pages.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.parent || "").toLowerCase().includes(q),
    );
  }, [pages, search]);

  const togglePage = (key: string) => {
    if (value.app_wide) return;
    setValue((v) =>
      v.page_keys.includes(key)
        ? { ...v, page_keys: v.page_keys.filter((k) => k !== key) }
        : { ...v, page_keys: [...v.page_keys, key] },
    );
  };

  const save = async () => {
    if (!value.title.trim() || !value.description.trim()) {
      toast.error("Titel och beskrivning krävs");
      return;
    }
    if (!value.app_wide && value.page_keys.length === 0) {
      toast.error("Välj minst en sida eller välj appövergripande");
      return;
    }
    setSaving(true);
    const payload = {
      tier: value.tier,
      title: value.title.trim(),
      description: value.description.trim(),
      app_wide: value.app_wide,
      page_keys: value.app_wide ? [] : value.page_keys,
    };
    const { error } = value.id
      ? await supabase.from("system_alerts" as any).update(payload).eq("id", value.id)
      : await supabase.from("system_alerts" as any).insert({ ...payload, created_by: user?.id });
    setSaving(false);
    if (error) {
      toast.error("Kunde inte spara: " + error.message);
      return;
    }
    toast.success(value.id ? "Meddelande uppdaterat" : "Meddelande publicerat");
    onSaved();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{value.id ? "Redigera meddelande" : "Publicera nytt meddelande"}</DialogTitle>
          <DialogDescription>
            Välj typ, skriv innehåll och bestäm var meddelandet ska visas.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <Label className="mb-2 block">Typ / brådska</Label>
            <div className="flex flex-wrap gap-2">
              {tiers.map((t) => {
                const meta = tierMeta[t];
                const active = value.tier === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setValue((v) => ({ ...v, tier: t }))}
                    className={cn(
                      "flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-all",
                      meta.banner,
                      active ? "ring-2 ring-offset-2 ring-foreground/40" : "opacity-70 hover:opacity-100",
                    )}
                  >
                    <span>{meta.emoji}</span>
                    <span className="font-medium">{meta.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label htmlFor="alert-title">Titel</Label>
            <Input
              id="alert-title"
              value={value.title}
              onChange={(e) => setValue((v) => ({ ...v, title: e.target.value }))}
              placeholder="T.ex. Planerat underhåll ikväll"
            />
          </div>

          <div>
            <Label htmlFor="alert-desc">Beskrivning</Label>
            <Textarea
              id="alert-desc"
              value={value.description}
              onChange={(e) => setValue((v) => ({ ...v, description: e.target.value }))}
              placeholder="Innehållet i meddelandet..."
              rows={3}
            />
          </div>

          <div>
            <Label className="mb-2 block">Var ska det visas?</Label>
            <div className="flex items-center gap-2 mb-3">
              <button
                type="button"
                onClick={() =>
                  setValue((v) => ({ ...v, app_wide: !v.app_wide, page_keys: [] }))
                }
                className={cn(
                  "flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors",
                  value.app_wide
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card hover:bg-secondary",
                )}
              >
                <Globe className="h-3.5 w-3.5" />
                Appövergripande
              </button>
              <div className={cn("relative flex-1", value.app_wide && "opacity-50 pointer-events-none")}>
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder="Sök sidor..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div
              className={cn(
                "flex flex-wrap gap-1.5 max-h-64 overflow-y-auto rounded-md border bg-muted/30 p-3",
                value.app_wide && "opacity-50 pointer-events-none",
              )}
            >
              {filteredPages.map((p) => {
                const selected = value.page_keys.includes(p.key);
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => togglePage(p.key)}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors",
                      selected
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-card hover:bg-secondary",
                    )}
                  >
                    {p.parent && (
                      <span
                        className={cn(
                          "rounded-sm px-1 py-0.5 text-[10px] leading-none",
                          selected ? "bg-primary-foreground/20" : "bg-muted text-muted-foreground",
                        )}
                      >
                        {p.parent}
                      </span>
                    )}
                    {p.name}
                  </button>
                );
              })}
              {filteredPages.length === 0 && (
                <p className="text-xs text-muted-foreground">Inga sidor matchade sökningen.</p>
              )}
            </div>
            {!value.app_wide && (
              <p className="mt-2 text-xs text-muted-foreground">
                {value.page_keys.length} sida{value.page_keys.length === 1 ? "" : "or"} valda
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Avbryt
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving ? "Sparar..." : value.id ? "Spara ändringar" : "Publicera"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
